import {
  Box,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  useGetSummarizedLoansQuery,
  useGetAggregateLoansMetadataQuery,
  SummarizedLoanFragmentGQLType,
} from 'packages/loans/requests/loans.gql.generated';
import { LoansTable } from 'packages/loans/ui/LoansTable';
import { SummaryCard } from 'packages/loans/ui/SummaryCard';
import { useGetPartiesByTypeAndCreditFacilityIdsQuery } from 'packages/organization/requests/organization.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';
import {
  LoansFiltersDrawerState,
  useLoansFilters,
} from '~/packages/loans/hooks/useLoansFilters';
import { LoansFiltersDrawer } from '~/packages/loans/ui/LoansFiltersDrawer';

export const LoansOverview = () => {
  const { facilities } = useCreditFacilitiesOrThrow();

  // Filters
  const [currentFilters, setCurrentFilters] = useLoansFilters({
    creditFacilityIds: facilities.map((facility) => facility.id),
  });
  const { data: lendersData } = useGetPartiesByTypeAndCreditFacilityIdsQuery({
    variables: {
      creditFacilityIds: currentFilters.selectedFacilityIds,
      partyType: CreditAgreement.DomainEntity.Items.PartyType.CapitalProvider,
    },
  });
  useEffect(() => {
    setCurrentFilters({
      lenderIds:
        lendersData?.partiesByTypeAndCreditFacilityIds.map(
          (parties) => parties.id,
        ) ?? [],
    });
  }, [setCurrentFilters, lendersData]);

  const filtersDerivedState: LoansFiltersDrawerState = useMemo(
    () => ({
      calculatedLoanStatuses: currentFilters.selectedLoanStatuses,
      maturityDateRange: currentFilters.selectedMaturityDateRange,
      originationDateRange: currentFilters.selectedOriginationDateRange,
      creditFacilityIds: currentFilters.selectedFacilityIds,
      lenderIds: currentFilters.selectedLenderIds,
    }),
    [
      currentFilters.selectedFacilityIds,
      currentFilters.selectedLoanStatuses,
      currentFilters.selectedMaturityDateRange,
      currentFilters.selectedOriginationDateRange,
      currentFilters.selectedLenderIds,
    ],
  );

  const aggregateMetadataQuery = useGetAggregateLoansMetadataQuery({
    fetchPolicy: 'network-only',
    variables: {
      filterParams: {
        capitalProviderIds: currentFilters.selectedLenderIds,
        createdAtDates: {
          startDate: currentFilters.selectedOriginationDateRange.startDate,
          endDate: currentFilters.selectedOriginationDateRange.endDate,
        },
        creditFacilityIds: currentFilters.selectedFacilityIds,
        calculatedLoanStatuses: currentFilters.selectedLoanStatuses,
        maturityDates: {
          startDate: currentFilters.selectedMaturityDateRange.startDate,
          endDate: currentFilters.selectedMaturityDateRange.endDate,
        },
        isFinal: true,
      },
    },
  });

  if (aggregateMetadataQuery.error) throw aggregateMetadataQuery.error;

  const summarizedLoansQuery = useGetSummarizedLoansQuery({
    fetchPolicy: 'network-only',
    variables: {
      filterParams: {
        creditFacilityIds: currentFilters.selectedFacilityIds,
        capitalProviderIds: currentFilters.selectedLenderIds,
        calculatedLoanStatuses: currentFilters.selectedLoanStatuses,
        isFinal: true,
        createdAtDates: {
          startDate: currentFilters.selectedOriginationDateRange.startDate,
          endDate: currentFilters.selectedOriginationDateRange.endDate,
        },
        maturityDates: {
          startDate: currentFilters.selectedMaturityDateRange.startDate,
          endDate: currentFilters.selectedMaturityDateRange.endDate,
        },
      },
    },
  });

  if (summarizedLoansQuery.error) throw summarizedLoansQuery.error;

  const loansWithPendingEdits = useMemo<SummarizedLoanFragmentGQLType[]>(() => {
    if (!summarizedLoansQuery.data?.loans) {
      return [];
    }

    return summarizedLoansQuery.data.loans.filter((loan) => {
      return loan.edits.destination?.status === 'pending';
    });
  }, [summarizedLoansQuery.data]);

  const {
    totalOutstandingBalance,
    totalAccruedInterest,
    totalAccruedServiceFees,
    totalProjectedInterest,
    totalProjectedServiceFees,
  } = aggregateMetadataQuery.data?.results ?? {};

  return (
    <Box padding={dashboardRoutePadding}>
      <HStack justify="space-between">
        <Heading>Loans</Heading>
        <HStack gap="1.5rem">
          {aggregateMetadataQuery.loading ? (
            <Skeleton h="3rem" w="10rem" />
          ) : (
            <LoansFiltersDrawer
              onFilterChange={setCurrentFilters}
              initialFilters={filtersDerivedState}
            />
          )}
        </HStack>
      </HStack>
      <Flex sx={{ gap: '1rem', flexWrap: 'wrap', width: '100%', mt: '2rem' }}>
        <SummaryCard
          loading={aggregateMetadataQuery.loading}
          name="Total Outstanding Balance"
          value={
            typeof totalOutstandingBalance === 'number'
              ? totalOutstandingBalance
              : '-'
          }
        />
        <SummaryCard
          loading={aggregateMetadataQuery.loading}
          name="Total Accrued Interest & Fees"
          value={
            typeof totalAccruedInterest === 'number' ||
            typeof totalAccruedServiceFees === 'number'
              ? (totalAccruedInterest ?? 0) + (totalAccruedServiceFees ?? 0)
              : '-'
          }
        />
        <SummaryCard
          loading={aggregateMetadataQuery.loading}
          name="Total Projected Interest & Fees"
          value={
            typeof totalProjectedInterest === 'number' ||
            typeof totalProjectedServiceFees === 'number'
              ? (totalProjectedInterest ?? 0) + (totalProjectedServiceFees ?? 0)
              : '-'
          }
        />
      </Flex>

      {/* //TODO: add loans chart / visuals @markhaghani */}
      {commonUtils.EnvUtils.isProduction() ? (
        <LoansTable
          loading={summarizedLoansQuery.loading}
          loans={summarizedLoansQuery.data?.loans ?? []}
        />
      ) : (
        <Tabs>
          <TabList>
            <Tab>All</Tab>
            <Tab>Pending Edits</Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              <LoansTable
                loading={summarizedLoansQuery.loading}
                loans={summarizedLoansQuery.data?.loans ?? []}
              />
            </TabPanel>
            <TabPanel p={0}>
              <LoansTable
                loading={summarizedLoansQuery.loading}
                loans={loansWithPendingEdits}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
};
